import React from 'react';
import { SkillLevelValuesEnum, useGetSkillLevelsQuery } from 'types/generated/client';
import { useModal } from 'hooks/useModal';
import HeartRate from 'svg/HeartRate';
import InfoCircle from 'svg/InfoCircle';
import PeopleColored from 'svg/PeopleColored';
import { VENUE_DISTANCE_IMPERIAL_OPTIONS } from 'screens/CourtFinder/types';
import { ButtonText } from 'components/Button';
import CertificationSelect from 'components/Explore/Lessons/LessonFiltersModal/CertificationSelect';
import {
  FilterDivider,
  SectionTitle,
} from 'components/Explore/Lessons/LessonFiltersModal/LessonFilters';
import {
  CERTIFICATIONS_BY_SPORT,
  MAX_PRICE,
  MIN_PRICE,
} from 'components/Explore/Lessons/constants';
import SkillLevelInfoModal from 'components/Explore/SkillLevelInfoModal';
import FilterBox from 'components/forms/FilterBox/FilterBox';
import { SliderNumberRange } from 'components/forms/Slider';
import Switch from 'components/forms/Switch';
import ClinicDatePicker from './ClinicDatePicker';
import { CLINIC_TYPE_OPTIONS, COURT_TYPE_OPTIONS } from '../constants';
import { ClinicTypeFilter, CourtTypeFilter } from '../enums';
import { ClinicFiltersProps } from '../props';
import { sortOptions, timeOptions } from '../utils';

const TYPE_ICONS: Record<ClinicTypeFilter, React.ReactNode> = {
  [ClinicTypeFilter.Clinic]: <PeopleColored className="h-4 w-5" width={24} height={16} />,
  [ClinicTypeFilter.Fitness]: <HeartRate className="h-4 w-4 text-violet-800" />,
  [ClinicTypeFilter.Camp]: <img src="/images/programming/camp.png" className="h-5 w-5" alt="" />,
};

const ClinicFilters: React.FC<ClinicFiltersProps> = ({
  priceMinimum,
  setPriceMinimum,
  priceMaximum,
  setPriceMaximum,
  selectedSport,
  distance,
  setDistance,
  selectedCertification,
  setSelectedCertification,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  sortBy,
  setSortBy,
  isYouthProgramming,
  setIsYouthProgramming,
  selectedTypes,
  setSelectedTypes,
  selectedSkillLevel,
  setSelectedSkillLevel,
  selectedCourtTypes,
  setSelectedCourtTypes,
}) => {
  const {
    isOpen: isSkillLevelInfoOpen,
    openModal: openSkillLevelInfo,
    closeModal: closeSkillLevelInfo,
  } = useModal();

  const { data: skillLevelsData } = useGetSkillLevelsQuery({ fetchPolicy: 'cache-first' });
  const skillLevels = (skillLevelsData?.skillLevels ?? []).filter((level) => level.isDisplayed);

  const certifications = CERTIFICATIONS_BY_SPORT[selectedSport] ?? [];

  const toggleType = (type: ClinicTypeFilter) => {
    setSelectedTypes((curr) =>
      curr.includes(type) ? curr.filter((t) => t !== type) : [...curr, type],
    );
  };

  const toggleCourtType = (type: CourtTypeFilter) => {
    setSelectedCourtTypes((curr) =>
      curr.includes(type) ? curr.filter((t) => t !== type) : [...curr, type],
    );
  };

  return (
    <div className="flex flex-auto flex-col gap-ds-xl overflow-y-auto px-4 py-6 md:px-ds-3xl">
      {/* Distance, Date, Time and Sort By stay mobile-only — on desktop they live in
          the header row above the list, which is why the Figma desktop modal hides
          them. They lead the modal because that is where the mobile design puts them.

          Divider rule used throughout: a divider renders exactly when its own section
          renders AND at least one section above it does, so the first visible section
          at any width never gets a stray leading hairline. Header breakpoints this
          mirrors (ExploreClinics.tsx): distance + date `md`, time + sort `xl`. */}
      <div className="md:hidden">
        <SectionTitle>Distance</SectionTitle>
        <div className="grid grid-cols-3 gap-4">
          {VENUE_DISTANCE_IMPERIAL_OPTIONS.map((newDistance) => (
            <FilterBox
              key={newDistance.id}
              label={newDistance.name}
              onClick={() => setDistance(newDistance)}
              isSelected={distance.id === newDistance.id}
            />
          ))}
        </div>
      </div>

      <FilterDivider className="md:hidden" />
      <div className="md:hidden">
        <SectionTitle>Date</SectionTitle>
        <ClinicDatePicker selectedDate={selectedDate} onDateChange={setSelectedDate} />
      </div>

      {/* Time survives to xl, but Distance/Date above it stop at md — so its divider
          only earns its place below md. */}
      <FilterDivider className="md:hidden" />
      <div className="xl:hidden">
        <SectionTitle>Time</SectionTitle>
        <div className="grid grid-cols-3 gap-4">
          {timeOptions.map((timeOption) => (
            <FilterBox
              key={timeOption.value}
              label={timeOption.label}
              onClick={() => setSelectedTime(timeOption.value)}
              isSelected={timeOption.value === selectedTime}
            />
          ))}
        </div>
      </div>

      <FilterDivider className="xl:hidden" />
      <div className="xl:hidden">
        <SectionTitle>Sort By</SectionTitle>
        <div className="grid grid-cols-3 gap-4">
          {sortOptions.map((sortOption) => (
            <FilterBox
              key={sortOption.value}
              label={sortOption.label}
              onClick={() => setSortBy(sortOption.value)}
              isSelected={sortOption.value === sortBy}
            />
          ))}
        </div>
      </div>

      {/* First always-visible section: at xl everything above is gone, so no divider. */}
      <FilterDivider className="xl:hidden" />
      {/* Toggle is flush right on mobile and sits beside its label from md up, which is
          where the two Figma variants differ. */}
      <div className="flex items-center justify-between gap-ds-xl md:justify-start">
        <SectionTitle>Youth programming</SectionTitle>
        <Switch
          isActive={isYouthProgramming}
          toggleIsActive={() => setIsYouthProgramming((curr) => !curr)}
          ariaDescription="Only show clinics intended for youth"
          // Matches SectionTitle's own mb-2 so both sit on the same centre line.
          className="!ml-0 mb-2 w-fit"
        />
      </div>

      <FilterDivider />
      <div>
        <SectionTitle>Type</SectionTitle>
        <div className="grid grid-cols-4 gap-4 max-sm:grid-cols-3">
          {CLINIC_TYPE_OPTIONS.map((option) => (
            <FilterBox
              key={option.value}
              label={option.label}
              icon={TYPE_ICONS[option.value]}
              onClick={() => toggleType(option.value)}
              isSelected={selectedTypes.includes(option.value)}
            />
          ))}
        </div>
      </div>

      {certifications.length > 0 && (
        <div>
          <FilterDivider className="mb-ds-xl" />
          <SectionTitle>Certifications</SectionTitle>
          <div className="mt-3 grid grid-cols-4 gap-4 max-sm:grid-cols-3">
            {certifications.map((certification) => (
              <CertificationSelect
                key={certification.value}
                title={certification.title}
                onClick={() =>
                  setSelectedCertification(
                    selectedCertification === certification.value ? null : certification.value,
                  )
                }
                isSelected={selectedCertification === certification.value}
                imgSrc={certification.imageSrc}
              />
            ))}
          </div>
        </div>
      )}

      <FilterDivider />
      <div>
        <div className="flex items-center justify-between">
          <SectionTitle>Skill level</SectionTitle>
          <ButtonText
            onClick={() => openSkillLevelInfo()}
            className="typography-product-button-label-medium mb-2 flex items-center gap-1 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
          >
            <InfoCircle className="h-4 w-4" />
            Learn more
          </ButtonText>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <FilterBox
            label="Any"
            onClick={() => setSelectedSkillLevel(null)}
            isSelected={selectedSkillLevel === null}
          />
          {skillLevels.map((level) => (
            <FilterBox
              key={level.id}
              label={level.displayName}
              onClick={() => setSelectedSkillLevel(level.id as SkillLevelValuesEnum)}
              isSelected={selectedSkillLevel === level.id}
            />
          ))}
        </div>
      </div>

      <FilterDivider />
      <div>
        <SectionTitle>Price</SectionTitle>
        <div className="pt-10">
          <SliderNumberRange
            rangeMinimum={MIN_PRICE}
            rangeMaximum={MAX_PRICE}
            valueMinimum={priceMinimum}
            valueMaximum={priceMaximum}
            setValueMinumum={(newMinimum) => setPriceMinimum(newMinimum)}
            setValueMaximum={(newMaximum) => setPriceMaximum(newMaximum)}
            step={1}
            decimals={0}
            prefix="$"
            isMaximumOpenEnded
          />
        </div>
      </div>

      <FilterDivider />
      <div>
        <SectionTitle>Court type</SectionTitle>
        {/* 3 columns with only 2 filled — matches the design's 176px tiles rather
            than stretching each to half the modal width. */}
        <div className="grid grid-cols-3 gap-4 max-sm:grid-cols-2">
          {COURT_TYPE_OPTIONS.map((option) => (
            <FilterBox
              key={option.value}
              label={option.label}
              onClick={() => toggleCourtType(option.value)}
              isSelected={selectedCourtTypes.includes(option.value)}
            />
          ))}
        </div>
      </div>

      <SkillLevelInfoModal isOpen={isSkillLevelInfoOpen} closeModal={closeSkillLevelInfo} />
    </div>
  );
};

export default ClinicFilters;
